import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AutobuyService } from '../AutobuyService';
import { Link } from 'react-router-dom';
import { config } from '../Constants';
import {
    Container, Typography, Box, Paper, Grid, Avatar,
    Button, Card, CardContent, Chip,

    Dialog, DialogTitle, DialogContent, DialogActions,
    DialogContentText, CircularProgress, Snackbar, Alert
} from '@mui/material';
import {
    WorkspacePremium as PremiumIcon,
    CreditCard, ShoppingCart, Email
} from '@mui/icons-material';

const URL = config.url;

const ProfilePage = () => {
    const { user, isAuthenticated, authToken, validateToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [credits, setCredits] = useState(0);
    const [trackedCount, setTrackedCount] = useState(0);
    const [, setRefreshingData] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionCompleted, setActionCompleted] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Use a single source of truth for subscription status
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    const fetchUserData = useCallback(async () => {
        if (!isAuthenticated || !authToken) return;

        try {
            setRefreshingData(true);

            // Fetch the latest user data from backend
            const response = await fetch(`${URL}/api/v1/auth/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.result === 'SUCCESS') {

                    // Update subscription status from the response
                    if (data.data && data.data.subscriptionStatus) {
                        setSubscriptionStatus(data.data.subscriptionStatus);
                    }
                }
            }

            // Also refresh the auth context
            await validateToken();

            // Fetch credit balance
            const userCredits = await AutobuyService.getUserCredits();
            setCredits(userCredits);

            // Fetch tracked items count
            const autobuys = await AutobuyService.getUserAutobuys();
            setTrackedCount(autobuys.length);
        } catch (error) {
            console.error('Error fetching user data:', error);
        } finally {
            setRefreshingData(false);
        }
    }, [isAuthenticated, authToken, validateToken]);

    // Initialize subscription status from user data
    useEffect(() => {
        if (user?.subscriptionStatus && !subscriptionStatus) {
            setSubscriptionStatus(user.subscriptionStatus);
        }
    }, [user, subscriptionStatus]);

    // After action completion, refetch data
    useEffect(() => {
        if (actionCompleted) {
            const timer = setTimeout(() => {
                fetchUserData();
                setActionCompleted(false);
            }, 1000); // Small delay to ensure backend state is updated

            return () => clearTimeout(timer);
        }
    }, [actionCompleted, fetchUserData]);

    // Initial data fetch
    useEffect(() => {
        const fetchData = async () => {
            if (!isAuthenticated) return;

            try {
                setLoading(true);
                await fetchUserData();
            } catch (error) {
                console.error('Error fetching user data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isAuthenticated, fetchUserData]);

    const handleCancelSubscription = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(`${URL}/api/v1/subscription/cancel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Immediately update UI state
                setSubscriptionStatus("canceling");

                setSnackbar({
                    open: true,
                    message: 'Your subscription has been canceled. You will have premium access until the end of your billing period.',
                    severity: 'success'
                });
                setCancelDialogOpen(false);

                // Set action completed flag to trigger refetch
                setActionCompleted(true);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error canceling subscription:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to cancel subscription. Please try again.',
                severity: 'error'
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleResumeSubscription = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(`${URL}/api/v1/subscription/resume`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Immediately update UI state
                setSubscriptionStatus("active");

                setSnackbar({
                    open: true,
                    message: 'Your subscription has been resumed. Your premium benefits will continue as normal.',
                    severity: 'success'
                });
                setResumeDialogOpen(false);

                // Set action completed flag to trigger refetch
                setActionCompleted(true);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to resume subscription');
            }
        } catch (error) {
            console.error('Error resuming subscription:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to resume subscription. Please try again.',
                severity: 'error'
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (!isAuthenticated) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Please sign in to view your profile
                    </Typography>
                </Paper>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    // Check if subscription is canceling
    const isSubscriptionCanceling = subscriptionStatus === "canceling";

    // Determine if premium is active
    const isPremiumActive = user?.premium && (subscriptionStatus === "active" || subscriptionStatus === "canceling");

    // Get premium expiry date
    const premiumExpiryDate = user?.premiumExpiry;

    const formatDateFromArray = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray) || dateArray.length !== 3) {
            return 'Invalid date';
        }

        try {
            const [year, month, day] = dateArray;
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date array:', error);
            return 'Invalid date';
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    My Profile
                </Typography>
            </Box>

            {/* User Information Card */}
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={2}>
                        <Avatar
                            src={user?.imageUrl}
                            alt={user?.name}
                            sx={{ width: 100, height: 100, mx: 'auto' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={10}>
                        <Typography variant="h5">{user?.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Email sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body1" color="text.secondary">
                                {user?.email}
                            </Typography>
                        </Box>
                        {isPremiumActive && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    icon={<PremiumIcon />}
                                    label="Premium Member"
                                    color="primary"
                                    sx={{ mr: 1 }}
                                />
                                {isSubscriptionCanceling && (
                                    <Chip
                                        label="Canceling"
                                        color="error"
                                        size="small"
                                    />
                                )}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Dashboard Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Subscription Card */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Subscription
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PremiumIcon sx={{ mr: 1, color: isPremiumActive ? 'gold' : 'text.secondary' }} />
                                <Typography>
                                    {isPremiumActive ? 'Premium' : 'Free User'}
                                </Typography>
                            </Box>

                            {isPremiumActive && premiumExpiryDate && (
                                <Typography variant="body2" color="text.secondary">
                                    {isSubscriptionCanceling ? (
                                        <>Premium access ends: {formatDateFromArray(premiumExpiryDate)}</>
                                    ) : (
                                        <>Renews: {formatDateFromArray(premiumExpiryDate)}</>
                                    )}
                                </Typography>
                            )}

                            {/* Subscription action button */}
                            <Box sx={{ mt: 2 }}>
                                {actionLoading ? (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        disabled
                                    >
                                        <CircularProgress size={24} />
                                    </Button>
                                ) : isPremiumActive ? (
                                    isSubscriptionCanceling ? (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => setResumeDialogOpen(true)}
                                            fullWidth
                                        >
                                            Resume Subscription
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => setCancelDialogOpen(true)}
                                            fullWidth
                                        >
                                            Cancel Subscription
                                        </Button>
                                    )
                                ) : (
                                    <Button
                                        variant="outlined"
                                        component={Link}
                                        to="/subscription"
                                        fullWidth
                                    >
                                        Upgrade to Premium
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Credits Card */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Autobuy Credits
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CreditCard sx={{ mr: 1 }} />
                                <Typography>
                                    {credits} Credit{credits !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/credits"
                                sx={{ mt: 2 }}
                                fullWidth
                            >
                                Manage Credits
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Tracked Items Card */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Tracked GPUs
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ShoppingCart sx={{ mr: 1 }} />
                                <Typography>
                                    {trackedCount} GPU{trackedCount !== 1 ? 's' : ''} tracked
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/my-tracked-stocks"
                                sx={{ mt: 2 }}
                                fullWidth
                            >
                                View Tracked GPUs
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Subscription Cancellation Dialog */}
            <Dialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                aria-labelledby="cancel-dialog-title"
                aria-describedby="cancel-dialog-description"
            >
                <DialogTitle id="cancel-dialog-title">
                    Cancel Your Premium Subscription?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cancel-dialog-description">
                        Are you sure you want to cancel your premium subscription?
                        <br /><br />
                        You'll continue to have premium access until the end of your current billing period.
                        After that, you'll be downgraded to a free account.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>
                        Keep Subscription
                    </Button>
                    <Button
                        onClick={handleCancelSubscription}
                        color="error"
                        autoFocus
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : 'Confirm Cancellation'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Subscription Resume Dialog */}
            <Dialog
                open={resumeDialogOpen}
                onClose={() => setResumeDialogOpen(false)}
                aria-labelledby="resume-dialog-title"
                aria-describedby="resume-dialog-description"
            >
                <DialogTitle id="resume-dialog-title">
                    Resume Your Premium Subscription?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="resume-dialog-description">
                        Are you sure you want to resume your premium subscription?
                        <br /><br />
                        Your subscription will continue as normal and you'll be billed on your next
                        renewal date.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResumeDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResumeSubscription}
                        color="primary"
                        autoFocus
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : 'Resume Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ProfilePage;